import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AUTH_ENABLED } from '../constants/features';

const TOKEN_KEY = 'cynopsis_token';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const applyToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
    axios.defaults.headers.common.Authorization = `Bearer ${token}`;
  } else {
    localStorage.removeItem(TOKEN_KEY);
    delete axios.defaults.headers.common.Authorization;
  }
};

const pickUser = (data) => {
  if (!data) return null;
  const { access_token: _t, ...user } = data;
  return user;
}; 

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(AUTH_ENABLED);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

  const persistSession = useCallback((data) => {
    const token = data?.access_token;
    const nextUser = pickUser(data);
    if (token) applyToken(token);
    setUser(nextUser);
    return nextUser;
  }, []);

  const checkAuth = useCallback(async () => {
    if (!AUTH_ENABLED) return;
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) applyToken(stored);
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {
      applyToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (!AUTH_ENABLED) {
      setUser(null);
      setLoading(false);
      return;
    }
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) applyToken(stored);
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      persistSession(response.data);
      return { success: true, user: pickUser(response.data) };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Login failed';
      if (typeof detail === 'string') message = detail;
      return { success: false, error: message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      persistSession(response.data);
      return { success: true, user: pickUser(response.data) };
    } catch (error) {
      const detail = error.response?.data?.detail;
      let message = 'Registration failed';
      if (typeof detail === 'string') message = detail;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      applyToken(null);
      setUser(null);
    }
  };

  const refreshUser = useCallback(async () => {
    if (!AUTH_ENABLED) return null;
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
      return response.data;
    } catch {
      return null;
    }
  }, [API]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        logout,
        checkAuth,
        refreshUser,
        authEnabled: AUTH_ENABLED,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
